import { ProductAppKind } from "@/apps/sidebar-item/__generated__/AppSidebarItemFragment.graphql"
import { useAppLevel } from "@/apps/util/appLevelContext"
import useConnectedProductApps from "@/apps/util/hooks/useConnectedProductApps"
import useFeatureFlags from "@utils/hook/useFeatureFlags"

type ConnectableAppConfig = {
  kind: ProductAppKind
  // Can multiple apps exist at the same time
  repeatable: boolean
  // can the app be added at the product-level?
  productLevel: boolean
  // can the app be added at the organization-level?
  organizationLevel: boolean
}

// NOTE: Changing the order of the apps in this array will change the order that they are rendered in CustomDiscoAppsList
// With the exception of "Slack", which gets rendered in IntegrationAppsList
const CONNECTABLE_APPS: ConnectableAppConfig[] = [
  {
    kind: "dashboard",
    repeatable: true,
    productLevel: false,
    organizationLevel: true,
  },
  {
    kind: "curriculum",
    repeatable: false,
    productLevel: true,
    organizationLevel: false,
  },
  {
    kind: "events",
    repeatable: false,
    productLevel: true,
    organizationLevel: false,
  },
  {
    kind: "organization_events",
    repeatable: false,
    productLevel: false,
    organizationLevel: true,
  },
  {
    kind: "posts",
    repeatable: true,
    productLevel: true,
    organizationLevel: true,
  },
  {
    kind: "member_directory",
    repeatable: true,
    productLevel: true,
    organizationLevel: true,
  },
  {
    kind: "group_directory",
    repeatable: true,
    productLevel: true,
    organizationLevel: true,
  },
  {
    kind: "chat_channel",
    repeatable: true,
    productLevel: true,
    organizationLevel: true,
  },
  {
    kind: "collection",
    repeatable: true,
    productLevel: true,
    organizationLevel: true,
  },
  {
    kind: "content",
    repeatable: true,
    productLevel: true,
    organizationLevel: true,
  },
  {
    kind: "link",
    repeatable: true,
    productLevel: true,
    organizationLevel: true,
  },
  {
    kind: "slack",
    repeatable: false,
    productLevel: true,
    organizationLevel: false,
  },
]

export default function useUnconnectedProductApps(): ProductAppKind[] {
  const { connectedApps } = useConnectedProductApps()
  const { product } = useAppLevel()
  const { dashboardApps } = useFeatureFlags()
  return CONNECTABLE_APPS.filter((app) => {
    // Only allow Custom page app if the feature flag is enabled
    if (app.kind === "dashboard" && !dashboardApps) return false

    // Filter out product-only apps when adding at organization level
    if (!product && !app.organizationLevel) return false

    // Filter out organization-only apps when adding at product level
    if (product && !app.productLevel) return false

    if (app.repeatable) return true

    return !connectedApps.has(app.kind)
  }).map((app) => app.kind)
}
